import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Signal,
} from '@angular/core';
import {
	ICmmsFilterComponent,
	ICmmsFilterEmittedValue,
	ICmmsFilterWithDataSetter,
	ICmmsFilterWithDefaultData,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/interfaces/filter-component.interface';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
	MatTree,
	MatTreeFlatDataSource,
	MatTreeFlattener,
	MatTreeNode,
	MatTreeNodeDef,
	MatTreeNodePadding,
	MatTreeNodeToggle,
} from '@angular/material/tree';
import { IBuilding, IBuildingFloor } from '@app/shared/interfaces/building.interfaces';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { getCurrentBuilding } from '@app/pages/my-building/state/my-bulding.selectors';
import { Store } from '@ngrx/store';
import { LocationTypes } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { buildingsList } from '@app/layout/state/layout.selectors';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';

import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AuSearchInputComponent } from '@app/shared/ui-components/au-search-input/au-search-input.component';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';
import { ILocation } from '@app/domain/types';

interface ILocationFlatNode {
	expandable: boolean;
	name: string;
	level: number;
	id: string;
}

type ILocationNode = IBuilding | IBuildingFloor | ILocation;
export type IEntity = ICmmsAsset | IWorkOrder | IRequest | IIncident | IFileEntity;

@Component({
	selector: 'location-tree-filter',
	templateUrl: './location-tree-filter.component.html',
	styleUrls: ['./location-tree-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatProgressSpinner,
		AuSearchInputComponent,
		MatTree,
		MatTreeNodeDef,
		MatTreeNode,
		MatTreeNodePadding,
		MatIconButton,
		MatCheckbox,
		MatTreeNodeToggle,
		MatIcon,
		NgxTolgeeModule,
	],
})
export class LocationTreeFilterComponent
	implements
		ICmmsFilterComponent,
		ICmmsFilterWithDefaultData,
		ICmmsFilterWithDataSetter,
		OnInit,
		OnDestroy
{
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly store$ = inject(Store);
	private readonly translateService = inject(TranslateService);

	@Input() data: {
		activePage: CmmsPages;
		columnName: string;
		options?: { [key: string]: any };
		preselectedValue?: string[];
	};
	@Output() filterValueChanged = new EventEmitter<ICmmsFilterEmittedValue>();

	loadingOptions = true;
	treeControl: FlatTreeControl<ILocationFlatNode>;
	locationsSelection = new SelectionModel<ILocationFlatNode>(true);
	dataSource: MatTreeFlatDataSource<ILocationNode, ILocationFlatNode>;
	filteredDataSource: MatTreeFlatDataSource<ILocationNode, ILocationFlatNode>;

	currentBuilding: Signal<{
		name: string;
		locationId: string;
		locationType: LocationTypes;
	}> = this.store$.selectSignal(getCurrentBuilding);

	private buildings = this.store$.selectSignal(buildingsList);

	private readonly treeFlattener: MatTreeFlattener<ILocationNode, ILocationFlatNode>;
	private readonly destroy$ = new Subject<void>();

	constructor() {
		this.treeFlattener = new MatTreeFlattener(
			(node: ILocationNode, level: number) => {
				const hasFloor = 'floor' in node && !!node.floor && node.floor.length > 0,
					hasRoom = 'room' in node && !!node.room && node.room.length > 0;
				return {
					expandable: hasFloor || hasRoom,
					name: node.name,
					level: level,
					id: node.id,
				};
			},
			(node: ILocationFlatNode) => node.level,
			(node: ILocationFlatNode) => node.expandable,
			(node: ILocationNode) =>
				('floor' in node && node.floor) || ('room' in node && node.room) || []
		);
		this.treeControl = new FlatTreeControl<ILocationFlatNode>(
			node => node.level,
			node => node.expandable
		);
		this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
	}

	hasChild = (_: number, node: ILocationFlatNode) => node.expandable;

	ngOnInit() {
		this.initData(this.buildings());

		if (this.data?.preselectedValue) {
			this.selectPreselectedValues();
		}

		this.locationsSelection.changed.pipe(takeUntil(this.destroy$)).subscribe(change => {
			this.filterValueChanged.emit(this.generateFilterEventValue(change.source.selected));
			this.cdr.markForCheck();
		});
	}

	selectPreselectedValues() {
		this.locationsSelection.clear();
		this.data.preselectedValue.forEach(id => {
			const node = this.treeControl.dataNodes.find(n => n.id === id);
			if (node) {
				this.locationsSelection.select(node);
			}
		});
	}

	updateFilteredData(searchText = '') {
		const filterValue = searchText.trim().toLowerCase();
		this.filteredDataSource.data = this.dataSource.data.filter(
			(buildingNode: ILocationNode) => this.matchesSearchText(buildingNode, filterValue)
		);

		this.loadingOptions = false;
		this.cdr.markForCheck();
	}

	matchesSearchText(node: ILocationNode, filterValue: string): boolean {
		return node.name.toLowerCase().includes(filterValue);
	}

	generateFilterEventValue(nodes: ILocationFlatNode[]): ICmmsFilterEmittedValue {
		const data = nodes.map(i => i.id);
		const label = nodes.map(i => i.name).join(', ');
		return {
			columnName: this.data.columnName,
			data,
			label,
		};
	}

	descendantsAllSelected(node: ILocationFlatNode): boolean {
		const descendants = this.treeControl.getDescendants(node);
		return descendants.every(child => this.locationsSelection.isSelected(child));
	}

	descendantsPartiallySelected(node: ILocationFlatNode): boolean {
		const descendants = this.treeControl.getDescendants(node);
		const result = descendants.some(child => this.locationsSelection.isSelected(child));
		return result && !this.descendantsAllSelected(node);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	toggleParent(node: ILocationFlatNode, event: { checked: boolean }) {
		const descendants = this.treeControl.getDescendants(node);
		const elements = [node, ...descendants];
		if (event.checked) {
			this.locationsSelection.select(...elements);
		} else {
			this.locationsSelection.deselect(...elements);
		}
	}

	private initData(locations: ILocationNode[]) {
		//Add a filter option for entities with no location.
		const noLocationFilterOption = {
			name: this.translateService.instant('no_location'),
			contentType: '',
			id: null,
		};
		let optionsList = [noLocationFilterOption, ...locations];

		if (this.data?.options?.selectedBuildingId) {
			optionsList = [
				optionsList.find(b => b.id === this.data.options?.selectedBuildingId),
			];
		}

		this.dataSource.data = optionsList;
		this.filteredDataSource = new MatTreeFlatDataSource(
			// We reuse here the same treeControl and treeFlattener as the main dataSource
			this.dataSource['_treeControl'],
			this.dataSource['_treeFlattener']
		);

		this.updateFilteredData();
	}

	findNodeAndDescendants(names = []) {
		const locationsWithChildren = names.map(name => {
			const nameArray = name.split('| ');
			const processedName = nameArray[nameArray.length - 1];
			const currentNode = this.treeControl.dataNodes.find(node =>
				node.name.includes(processedName)
			);
			if (!currentNode) {
				return [];
			}
			return [currentNode, ...this.treeControl.getDescendants(currentNode)];
		});

		return locationsWithChildren.flat();
	}

	getLocationName = (entity: IEntity): string[] => {
		const locationsProperty = 'locations' in entity ? 'locations' : 'location';
		const locations = Array.isArray(entity[locationsProperty])
			? entity[locationsProperty]
			: [entity[locationsProperty]];

		if (this.currentBuilding()) {
			return [this.currentBuilding().name];
		}
		return locations.map(location => location.name);
	};

	getDefaultFilterValue(entity: IEntity[]) {
		if (!entity.length) {
			return;
		}
		const locationList = entity.flatMap(entityItem => {
			return this.getLocationName(entityItem);
		});
		this.initData(this.buildings());
		return of(this.generateFilterEventValue(this.findNodeAndDescendants(locationList)));
	}

	setFilterValue(entity: IEntity | IEntity[]) {
		let locationList;
		if (Array.isArray(entity)) {
			locationList = entity.flatMap(entityItem => {
				return this.getLocationName(entityItem);
			});
		} else {
			locationList = this.getLocationName(entity);
		}
		this.locationsSelection.select(...this.findNodeAndDescendants(locationList));
	}
}
